/**
 * A Pages container's shared children: the direct children registered in
 * `shared`, which every page shows, and the per-page state each page assigns
 * them (`pages[i].states`). These are the edits that make a page drive states.
 *
 * Each operation applies its change, announces it on the container, and
 * returns the undo command for it without pushing it, so a host bundles it
 * with its own steps; null means nothing changed. Moves go through the
 * authoring `setParent` op, which keeps the object, its Base and its state
 * poses where they are in the world, and refuses as it does (play, the host's
 * reparent policy, a parent inside a scene reference): a refused move changes
 * nothing here either.
 */

import type { SceneObject } from '../../core/SceneObject';
import { Pages } from '../../behaviors/pages';
import { Properties } from '../../behaviors/properties';
import type { PagesData } from '../../scenedata/behaviors/pagesdata';
import type { StateDefinition } from '../../scenedata/behaviors/propertiesdata';
import type { UndoCommand } from '../undo/UndoCommand';
import { AddBehaviorCommand } from '../undo/AddBehaviorCommand';
import { BehaviorCommitCommand } from '../undo/BehaviorCommitCommand';
import { CreateObjectCommand } from '../undo/CreateObjectCommand';
import { dispatchBehaviorChanged } from '../undo/editor-events';
import { setParent } from '../authoring/objectOps';
import { bundle, rollBack } from '../authoring/steps';
import { setAssignmentForTarget, withoutTarget } from './pagesModel';

/** The state a page assigns to hide a shared child there: `{ values: {}, active: false }`. */
export const HIDDEN_STATE = 'hidden';

/** The lazily created child that holds a container's shared content. */
export const SHARED_GROUP_NAME = 'Shared';

// The group each container uses this session, so a rename keeps it; across
// sessions the name finds it again.
const sessionSharedGroups = new WeakMap<SceneObject, SceneObject>();

/** Tell the container's listeners its Pages data changed. */
export function notifyPagesChanged(container: SceneObject): void {
    dispatchBehaviorChanged({ objectPath: container.getPath(), behaviorType: 'Pages', action: 'updated' });
}

/** The container's Shared group, if it has one: the one this session uses, else a shared child named {@link SHARED_GROUP_NAME}. */
export function findSharedGroup(container: SceneObject): SceneObject | null {
    const pages = container.getBehavior(Pages);
    if (!pages) return null;
    const shared = (pages.getData().shared ?? [])
        .map(path => container.traceScene.resolveSceneObject(path, container))
        .filter((object): object is SceneObject => !!object);
    const remembered = sessionSharedGroups.get(container);
    if (remembered && shared.includes(remembered)) return remembered;
    return shared.find(object => object.name === SHARED_GROUP_NAME) ?? null;
}

/**
 * The container's Shared group, created and registered when it has none, with
 * the commands that made it (empty when it existed). The registration commit
 * (`registerSharedChild`) announces the change on the container. Null for a
 * container without Pages or one that takes its pages from its first child.
 */
export async function ensureSharedGroup(container: SceneObject): Promise<{ group: SceneObject; commands: UndoCommand[] } | null> {
    const pages = container.getBehavior(Pages);
    if (!pages || pages.getData().pageSource === 'firstChild') return null;
    const existing = findSharedGroup(container);
    if (existing) {
        sessionSharedGroups.set(container, existing);
        return { group: existing, commands: [] };
    }
    const scene = container.traceScene;
    const group = await scene.createSceneObject(SHARED_GROUP_NAME, container);
    let created: UndoCommand | null = null;
    let registered: UndoCommand | null;
    try {
        group.captureTransform();
        group.refreshSceneData();
        created = new CreateObjectCommand(group, container, group.getSceneObjectData(), []);
        registered = await registerSharedChild(container, group);
    } catch (error) {
        if (created) await takeBack(container, [created], error);
        else scene.deleteSceneObject(group);
        throw error;
    }
    sessionSharedGroups.set(container, group);
    return { group, commands: registered ? [created, registered] : [created] };
}

/**
 * Register `object`, a direct child of the container, in `shared`: a copy of
 * a shared child landing beside it, or the new Shared group. Until it is
 * listed, a direct child counts as a page, so registering a page takes it out
 * of the page order; callers pass only children they made. Null when it is
 * listed already, is not a direct child, or the container has no shared
 * children (no Pages, or `firstChild` mode).
 */
export async function registerSharedChild(container: SceneObject, object: SceneObject): Promise<UndoCommand | null> {
    const pages = pagingOf(container);
    const path = container.traceScene.getRelativePath(object, container);
    if (!pages || !path || object.getParent() !== container) return null;
    const before = pages.getData();
    if ((before.shared ?? []).includes(path)) return null;
    return commitPages(container, pages, { ...before, shared: [...(before.shared ?? []), path] });
}

/**
 * For shared content leaving the scene: drop every assignment targeting it or
 * a node under it, and its `shared` entry when it is a shared child. A path
 * left behind would bind whatever lands there next. Null when `object` is not
 * shared content (a shared child, a member of the Shared group, or a node
 * under one) or nothing names it.
 */
export async function pruneSharedEntryForDelete(object: SceneObject): Promise<UndoCommand | null> {
    let container = object.getParent();
    while (container && !container.getBehavior(Pages)) container = container.getParent();
    const pages = container && pagingOf(container);
    const path = container && object.traceScene.getRelativePath(object, container);
    if (!container || !pages || !path || !isSharedContent(container, pages, object)) return null;
    const before = pages.getData();
    const after = (before.shared ?? []).includes(path) ? unlisted(before, path) : { ...before, pages: withoutTarget(before.pages, path) };
    return commitPages(container, pages, after);
}

/**
 * Move content of a page to the container and register it in `shared`. It
 * shows on every page, with no assignment yet: what its page assigned it at
 * its old place is dropped. Null for anything else.
 */
export async function makeShared(container: SceneObject, object: SceneObject): Promise<UndoCommand | null> {
    const pages = pagingOf(container);
    const from = container.traceScene.getRelativePath(object, container);
    if (!pages || !from || !isPageContent(container, object)) return null;
    const moved = await move(object, container);
    if (!moved) return null;
    return finish(container, `Share ${object.name} across pages`, moved, () => {
        const before = pages.getData();
        const path = container.traceScene.getRelativePath(object, container);
        const shared = path && !(before.shared ?? []).includes(path) ? [...(before.shared ?? []), path] : before.shared;
        return commitPages(container, pages, { ...before, shared, pages: withoutTarget(before.pages, from) });
    });
}

/**
 * Move a shared child into `page`, dropping its `shared` entry and every
 * assignment targeting it. Its states stay on it. Null unless `object` is a
 * shared child and `page` one of the pages.
 */
export async function unshare(container: SceneObject, object: SceneObject, page: SceneObject): Promise<UndoCommand | null> {
    const pages = pagingOf(container);
    const path = container.traceScene.getRelativePath(object, container);
    if (!pages || !path || !isPage(container, page) || !(pages.getData().shared ?? []).includes(path)) return null;
    const moved = await move(object, page);
    if (!moved) return null;
    return finish(container, `Unshare ${object.name}`, moved, () => commitPages(container, pages, unlisted(pages.getData(), path)));
}

/**
 * Move content of a page into the container's Shared group, making the group
 * when there is none, and drop what its page assigned it at its old place.
 * Null for anything else.
 */
export async function shareIntoGroup(container: SceneObject, object: SceneObject): Promise<UndoCommand | null> {
    const pages = pagingOf(container);
    const from = container.traceScene.getRelativePath(object, container);
    if (!pages || !from || !isPageContent(container, object)) return null;
    const ensured = await ensureSharedGroup(container);
    if (!ensured) return null;
    // The group made for this share goes with a move that is refused or faults.
    const done = [...ensured.commands];
    let moved: UndoCommand | null;
    try {
        moved = await move(object, ensured.group);
    } catch (error) {
        await takeBack(container, done, error);
        throw error;
    }
    if (!moved) {
        await takeBack(container, done, new Error(`[sharedChildren] moving "${object.name}" into the Shared group was refused`));
        return null;
    }
    done.push(moved);
    try {
        // What the page assigned the object at its old place no longer names it.
        const before = pages.getData();
        const pruned = await commitPages(container, pages, { ...before, pages: withoutTarget(before.pages, from) });
        if (pruned) done.push(pruned);
    } catch (error) {
        await takeBack(container, done, error);
        throw error;
    }
    return bundle(`Share ${object.name} across pages`, done);
}

/**
 * Move `object` out of the Shared group into `page`, dropping every
 * assignment targeting it or a node under it. The group stays. Null unless
 * `object` sits in the Shared group and `page` is one of the pages.
 */
export async function unshareFromGroup(container: SceneObject, object: SceneObject, page: SceneObject): Promise<UndoCommand | null> {
    const pages = pagingOf(container);
    const path = container.traceScene.getRelativePath(object, container);
    const group = findSharedGroup(container);
    if (!pages || !path || !group || object.getParent() !== group || !isPage(container, page)) return null;
    const moved = await move(object, page);
    if (!moved) return null;
    return finish(container, `Unshare ${object.name}`, moved, () => {
        const before = pages.getData();
        return commitPages(container, pages, { ...before, pages: withoutTarget(before.pages, path) });
    });
}

/**
 * Make page `pageIndex` show `state` on the shared child `object`, or Base
 * with null. {@link HIDDEN_STATE} hides it there: the state is created when
 * missing, and made to hide when a page edit has since shown it, since another
 * page assigning it means "hidden". Null unless `object` is shared content
 * (a shared child or a node under one). Throws a `RangeError`, changing
 * nothing, for an index that names no page.
 */
export async function setSharedAssignment(
    container: SceneObject,
    object: SceneObject,
    pageIndex: number,
    state: string | null,
): Promise<UndoCommand | null> {
    const pages = pagingOf(container);
    const path = container.traceScene.getRelativePath(object, container);
    if (!pages || !path || !isSharedContent(container, pages, object)) return null;
    const pageCount = pages.getPageNodes().length;
    if (!Number.isInteger(pageIndex) || pageIndex < 0 || pageIndex >= pageCount) {
        throw new RangeError(`[setSharedAssignment] "${container.name}" has no page ${pageIndex} (it has ${pageCount})`);
    }
    const steps: UndoCommand[] = [];
    try {
        if (state === HIDDEN_STATE) await ensureHiddenState(object, steps);
        const before = pages.getData();
        const assigned = setAssignmentForTarget(before.pages, pageIndex, path, state, pageCount);
        const commit = await commitPages(container, pages, { ...before, pages: assigned });
        if (commit) steps.push(commit);
    } catch (error) {
        await takeBack(container, steps, error);
        throw error;
    }
    return steps.length > 0 ? bundle(`Set ${object.name} page ${pageIndex + 1} state`, steps) : null;
}

// ── Steps ────────────────────────────────────────────────────────────────────

/**
 * The container's Pages when it pages its own children, the only mode with
 * shared children: in `firstChild` mode the pages are its first child's
 * children and `shared` is not read. Internal to `editor/pages`.
 */
export function pagingOf(container: SceneObject): Pages | null {
    const pages = container.getBehavior(Pages);
    return pages && pages.getData().pageSource !== 'firstChild' ? pages : null;
}

/** Whether `object` is one of the container's pages, which a move would take out of the page order. */
function isPage(container: SceneObject, object: SceneObject): boolean {
    return pagingOf(container)?.getPageNodes().includes(object.transform) ?? false;
}

/** The container's direct child `object` is, or sits under; null outside the container. */
function rootUnder(container: SceneObject, object: SceneObject): SceneObject | null {
    let node: SceneObject | null = object;
    while (node && node.getParent() !== container) node = node.getParent();
    return node;
}

/** Whether `object` is inside a page (not the page itself). */
function isPageContent(container: SceneObject, object: SceneObject): boolean {
    const root = rootUnder(container, object);
    return !!root && root !== object && isPage(container, root);
}

/** Whether `object` is a shared child, or a node under one: under a direct child `shared` resolves to. */
function isSharedContent(container: SceneObject, pages: Pages, object: SceneObject): boolean {
    const root = rootUnder(container, object);
    return !!root && (pages.getData().shared ?? []).some(path => container.traceScene.resolveSceneObject(path, container) === root);
}

async function move(object: SceneObject, parent: SceneObject): Promise<UndoCommand | null> {
    const result = await setParent(object, parent);
    return 'refused' in result ? null : result.command;
}

/** Bundle a move with the Pages step after it, taking the move back if that step faults. */
async function finish(container: SceneObject, label: string, moved: UndoCommand, then: () => Promise<UndoCommand | null>): Promise<UndoCommand> {
    try {
        const next = await then();
        return bundle(label, next ? [moved, next] : [moved]);
    } catch (error) {
        await takeBack(container, [moved], error);
        throw error;
    }
}

/**
 * Take back `done`, newest first, and announce it on the container: undo runs
 * silent, and listeners may have heard of a step being taken back. The
 * announcement goes out even when the taking back fails. Internal to
 * `editor/pages`.
 */
export async function takeBack(container: SceneObject, done: readonly UndoCommand[], cause: unknown): Promise<void> {
    try {
        await rollBack(done, cause);
    } finally {
        if (done.length > 0) notifyPagesChanged(container);
    }
}

/** `data` without `path` in `shared`, nor any assignment targeting it. */
function unlisted(data: PagesData, path: string): PagesData {
    const shared = (data.shared ?? []).filter(entry => entry !== path);
    return { ...data, shared: shared.length > 0 ? shared : undefined, pages: withoutTarget(data.pages, path) };
}

/** Commit `after` to the container's Pages and return its command; null, committing nothing, when it is the data there now. Internal to `editor/pages`. */
export async function commitPages(container: SceneObject, pages: Pages, after: PagesData): Promise<UndoCommand | null> {
    const before = pages.getData();
    if (JSON.stringify(after) === JSON.stringify(before)) return null;
    const errors = await pages.commit(after);
    if (errors.length > 0) throw new Error(`[pages] Pages refused the patch: ${errors.map(e => e.message).join('; ')}`);
    notifyPagesChanged(container);
    return new BehaviorCommitCommand(container, 'Pages', before, pages.getData());
}

/** Make `object`'s {@link HIDDEN_STATE} hide, adding it, and Properties, when missing. Appends to `steps` as it goes. */
async function ensureHiddenState(object: SceneObject, steps: UndoCommand[]): Promise<void> {
    let props = object.getBehavior(Properties);
    if (!props) {
        props = await object.addBehavior(Properties);
        steps.push(new AddBehaviorCommand(object, 'Properties', props.getData()));
    }
    const before = props.getData();
    const current = before.states?.[HIDDEN_STATE];
    if (current?.active === false) return;
    const hidden: StateDefinition = { ...(current ?? { values: {} }), active: false };
    const errors = await props.commit({ ...before, states: { ...before.states, [HIDDEN_STATE]: hidden } });
    if (errors.length > 0) throw new Error(`[sharedChildren] Properties refused the patch: ${errors.map(e => e.message).join('; ')}`);
    steps.push(new BehaviorCommitCommand(object, 'Properties', before, props.getData()));
}
